import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowRight,
  Building2,
  CalendarClock,
  CheckCircle2,
  Clock,
  DollarSign,
  HardHat,
  Plus,
  RefreshCw,
  Users,
} from 'lucide-react';
import { AreaChart, Bars, HBarList } from '../components/charts.jsx';
import { Helper, JobStatusBadge, PageHead, PriorityFlag, Says } from '../components/bits.jsx';
import {
  Avatar,
  Badge,
  Button,
  Card,
  CardBody,
  CardHead,
  EmptyState,
  Loading,
  cx,
} from '../components/ui.jsx';
import { usePoll } from '../lib/hooks.js';
import { MEANS, countOf, dollars, plural, whenPlain } from '../lib/plain.js';

export default function Dashboard() {
  const navigate = useNavigate();
  const { data, loading, refresh } = usePoll('/reports/dashboard', { params: { days: 30 }, ms: 60_000 });

  const kpis = data?.kpis;
  const alerts = data?.alerts || {};

  const series = data?.revenueSeries || [];
  const chart = useMemo(
    () => ({
      labels: series.map((point) =>
        new Date(point.day).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      ),
      series: [
        { key: 'revenue', label: 'We charged customers', color: '#ff8a1f', values: series.map((p) => Number(p.revenue) || 0) },
        { key: 'cost', label: 'We paid lumpers', color: '#5aa9ff', values: series.map((p) => Number(p.cost) || 0) },
        { key: 'margin', label: 'Left over for us', color: '#34d399', values: series.map((p) => Number(p.margin) || 0) },
      ],
    }),
    [series],
  );

  const dailyBars = (data?.dailyOps || []).map((d) => ({
    label: new Date(d.day).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    value: d.jobs,
  }));

  if (loading && !data) {
    return (
      <>
        <PageHead title="Today" subtitle="Getting the latest…" />
        <Loading rows={5} />
      </>
    );
  }

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
  const jobsToday = kpis?.jobsToday || 0;
  const unfilled = kpis?.unfilled || 0;
  const freeNow = kpis?.availableNow || 0;

  const things = [
    alerts.atRisk?.length
      ? {
          tone: 'red',
          icon: AlertTriangle,
          title: plural(alerts.atRisk.length, 'job starts soon and nobody is booked', 'jobs start soon with nobody booked'),
          body: alerts.atRisk[0]
            ? `The next one is ${alerts.atRisk[0].customerName}, ${whenPlain(alerts.atRisk[0].windowStart).toLowerCase()}.`
            : '',
          action: { label: 'Find someone', to: '/app/dispatch' },
        }
      : null,
    alerts.overdueInvoices?.length
      ? {
          tone: 'amber',
          icon: DollarSign,
          title: `${plural(alerts.overdueInvoices.length, 'customer is', 'customers are')} late paying`,
          body: `Altogether ${dollars(alerts.overdueInvoices.reduce((sum, i) => sum + Number(i.balance || 0), 0))} owed to us.`,
          action: { label: 'See who owes', to: '/app/invoices' },
        }
      : null,
    alerts.newApplicants?.length
      ? {
          tone: 'blue',
          icon: HardHat,
          title: plural(alerts.newApplicants.length, 'person wants to work with us', 'people want to work with us'),
          body: `${alerts.newApplicants[0].name} applied ${new Date(alerts.newApplicants[0].appliedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}.`,
          action: { label: 'Look at them', to: '/app/onboarding' },
        }
      : null,
    alerts.expiringDocs?.length
      ? {
          tone: 'violet',
          icon: Clock,
          title: `${plural(alerts.expiringDocs.length, 'lumper has', 'lumpers have')} paperwork running out`,
          body: `${alerts.expiringDocs[0].lumperName}'s ${alerts.expiringDocs[0].label.toLowerCase()} runs out in ${Math.max(alerts.expiringDocs[0].daysLeft, 0)} days.`,
          action: { label: 'Check paperwork', to: '/app/lumpers' },
        }
      : null,
    alerts.disputes?.length
      ? {
          tone: 'amber',
          icon: AlertTriangle,
          title: plural(alerts.disputes.length, 'problem was reported', 'problems were reported'),
          body: 'A crew or a customer was not happy with a job. Worth a phone call.',
          action: { label: 'See the problems', to: '/app/reports' },
        }
      : null,
    alerts.newClients?.length
      ? {
          tone: 'green',
          icon: Building2,
          title: plural(alerts.newClients.length, 'business signed up on the website', 'businesses signed up on the website'),
          body: alerts.newClients[0]
            ? `${alerts.newClients[0].name} came in ${new Date(alerts.newClients[0].createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}. Worth a call before they need a crew.`
            : '',
          action: { label: 'See the account', to: '/app/customers' },
        }
      : null,
  ].filter(Boolean);

  return (
    <>
      <PageHead
        title="Today"
        subtitle={`${greeting}. ${countOf(jobsToday, 'job')} on today. Across all the open work, ${countOf(unfilled, 'job')} still need someone to take them, and ${countOf(freeNow, 'lumper')} are free right now.`}
        actions={
          <>
            <Button variant="outline" size="sm" icon={RefreshCw} onClick={refresh}>Refresh</Button>
            <Button variant="primary" size="sm" icon={Plus} onClick={() => navigate('/app/jobs?new=1')}>
              Add a job
            </Button>
          </>
        }
      />

      {things.length ? (
        <div className="col gap-12 mb-20 stagger">
          {things.map((thing) => (
            <Says key={thing.title} tone={thing.tone} icon={thing.icon}>
              <div className="row between gap-12 wrap">
                <div className="grow">
                  <div className="says-title">{thing.title}</div>
                  {thing.body ? <div className="says-body">{thing.body}</div> : null}
                </div>
                <Link to={thing.action.to}>
                  <Button size="sm" variant="outline" trailing={<ArrowRight size={13} />}>
                    {thing.action.label}
                  </Button>
                </Link>
              </div>
            </Says>
          ))}
        </div>
      ) : (
        <Says tone="green" icon={CheckCircle2} className="mb-20">
          <div className="says-title">Nothing needs you right now</div>
          <div className="says-body">Every job today has someone booked, nobody is late paying, and all paperwork is up to date.</div>
        </Says>
      )}

      <div className="grid grid-4 mb-24">
        <div className="bignum">
          <span className="bignum-label">Jobs today</span>
          <span className="bignum-value">{jobsToday}</span>
          <span className="bignum-note">
            {kpis?.inFlight ? `${kpis.inFlight} being worked on right now` : 'Nothing started yet'}
          </span>
        </div>
        <div className="bignum" data-tone={unfilled ? 'amber' : 'green'}>
          <span className="bignum-label">Jobs nobody has taken</span>
          <span className="bignum-value" style={{ color: unfilled ? 'var(--amber)' : 'var(--green)' }}>{unfilled}</span>
          <span className="bignum-note">
            {unfilled ? 'open jobs waiting for someone' : 'every open job has someone'}
          </span>
        </div>
        <div className="bignum">
          <span className="bignum-label">Lumpers free now</span>
          <span className="bignum-value">{freeNow}</span>
          <span className="bignum-note">of {kpis?.activeLumpers || 0} on the roster</span>
        </div>
        <div className="bignum">
          <span className="bignum-label">Billed this month</span>
          <span className="bignum-value">{dollars(kpis?.revenue30)}</span>
          <span className="bignum-note">
            {dollars(kpis?.margin30)} left after paying lumpers
          </span>
        </div>
      </div>

      <Card className="mb-20">
        <CardHead
          title="Today's jobs"
          subtitle="Earliest first, so you can see who is due where"
          icon={CalendarClock}
          actions={
            <Link to="/app/jobs">
              <Button variant="ghost" size="sm" trailing={<ArrowRight size={13} />}>All jobs</Button>
            </Link>
          }
        />
        <div className="table-wrap">
          <table className="table plain">
            <thead>
              <tr>
                <th className="narrow">Time</th>
                <th>Job</th>
                <th>Where</th>
                <th>Who is doing it</th>
                <th className="narrow">Status</th>
                <th className="right">Pay</th>
              </tr>
            </thead>
            <tbody>
              {(data?.upcoming || []).map((job) => (
                <tr key={job.id} className="clickable" onClick={() => navigate(`/app/jobs/${job.id}`)}>
                  <td className="narrow">
                    <div className="col">
                      <span className="num" style={{ fontWeight: 600 }}>
                        {new Date(job.windowStart).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                      </span>
                      <span className="dimmer" style={{ fontSize: 'var(--fs-xs)' }}>
                        {new Date(job.windowStart).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                      </span>
                    </div>
                  </td>
                  <td>
                    <div className="col gap-2">
                      <span className="row gap-6">
                        <span style={{ fontWeight: 600 }}>{job.customerName}</span>
                        <PriorityFlag priority={job.priority} />
                      </span>
                      <span className="dim" style={{ fontSize: 'var(--fs-xs)' }}>
                        {job.palletsExpected} pallets to unload
                      </span>
                    </div>
                  </td>
                  <td>
                    <div className="col gap-2">
                      <span className="truncate" style={{ fontSize: 'var(--fs-sm)' }}>{job.siteName}</span>
                      <span className="dimmer" style={{ fontSize: 'var(--fs-xs)' }}>{job.siteCity}, {job.siteState}</span>
                    </div>
                  </td>
                  <td>
                    {job.lumperName ? (
                      <span className="row gap-8">
                        <Avatar name={job.lumperName} tone={job.lumperPhotoColor || 'blue'} size="sm" />
                        <span className="truncate" style={{ fontSize: 'var(--fs-sm)' }}>{job.lumperName}</span>
                      </span>
                    ) : (
                      <Badge tone="amber" dot>Nobody yet</Badge>
                    )}
                  </td>
                  <td className="narrow"><JobStatusBadge status={job.status} size="sm" dot={false} short /></td>
                  <td className="right num">{dollars(job.lumperPay)}</td>
                </tr>
              ))}
              {!data?.upcoming?.length ? (
                <tr>
                  <td colSpan={6}>
                    <EmptyState icon={CheckCircle2} title="No jobs booked right now">
                      Add a job and we will help you find someone to do it.
                    </EmptyState>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </Card>

      <details className="more">
        <summary>More numbers (for the owner)</summary>
        <div className="col gap-20" style={{ padding: 18 }}>
          <div className="split">
            <Card flat>
              <CardHead title="Money coming in and out" subtitle="Each day over the last 30 days" />
              <CardBody>
                <AreaChart series={chart.series} labels={chart.labels} height={240} valueFormat={(v) => dollars(v)} />
              </CardBody>
            </Card>
            <Card flat>
              <CardHead title="How busy each day was" subtitle="Number of jobs per day" />
              <CardBody>
                <Bars data={dailyBars} height={160} />
              </CardBody>
            </Card>
          </div>

          <div className="grid grid-3">
            <div className="bignum">
              <span className="bignum-label">We keep for every dollar</span>
              <span className="bignum-value">{(kpis?.marginPct || 0).toFixed(0)}¢</span>
              <span className="bignum-note">{MEANS.margin}</span>
            </div>
            <div className="bignum">
              <span className="bignum-label">Jobs that got someone</span>
              <span className="bignum-value">{kpis?.fillRate || 0}%</span>
              <span className="bignum-note">{MEANS.fillRate}</span>
            </div>
            <div className="bignum">
              <span className="bignum-label">Waiting to be paid out</span>
              <span className="bignum-value">{dollars(kpis?.payPending)}</span>
              <span className="bignum-note">{MEANS.waitingToPay}</span>
            </div>
          </div>

          <div className="split">
            <Card flat>
              <CardHead title="Best customers this month" />
              <CardBody>
                <HBarList
                  rows={(data?.topCustomers || []).map((c) => ({ label: c.name, value: c.revenue }))}
                  format={(v) => dollars(v)}
                  labelWidth={150}
                />
              </CardBody>
            </Card>
            <Card flat>
              <CardHead title="Busiest lumpers this month" subtitle="Jobs finished, and what they earned" />
              <CardBody className="col gap-12">
                {(data?.topLumpers || []).map((lumper) => (
                  <Link key={lumper.id} to={`/app/lumpers/${lumper.id}`} className="row gap-12">
                    <Avatar name={lumper.name} tone={lumper.photoColor || 'amber'} size="sm" />
                    <div className="grow truncate">
                      <div className="truncate" style={{ fontSize: 'var(--fs-sm)', fontWeight: 600 }}>{lumper.name}</div>
                      <div className="dim" style={{ fontSize: 'var(--fs-xs)' }}>
                        {countOf(lumper.jobs, 'job')} · {Number(lumper.avgHours || 0).toFixed(1)} hours each
                      </div>
                    </div>
                    <span className="num" style={{ fontSize: 'var(--fs-sm)' }}>{dollars(lumper.pay)}</span>
                  </Link>
                ))}
                {!data?.topLumpers?.length ? <span className="dim">No finished jobs this month yet.</span> : null}
              </CardBody>
            </Card>
          </div>

          <Helper>
            These numbers only count jobs that are finished. “Unpaid invoices” above is money customers still owe us.
          </Helper>
        </div>
      </details>

      <div className="row gap-8 wrap mt-20">
        <Link to="/app/onboarding"><Button size="sm" variant="outline" icon={HardHat}>New people to look at</Button></Link>
        <Link to="/app/notifications"><Button size="sm" variant="outline" icon={Users}>Send a message to lumpers</Button></Link>
        <Link to="/app/payroll"><Button size="sm" variant="outline" icon={DollarSign}>Pay lumpers</Button></Link>
      </div>
    </>
  );
}
